import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance with interceptor for auth token
const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Silent token refresh logic ────────────────────────────────────────────────
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
};

const triggerLogout = () => {
  window.dispatchEvent(new CustomEvent('auth:session_replaced'));
};

// Global response interceptor — handle 401s with silent refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // session_replaced: another device logged in — force logout immediately
    // BUT only if the rejected token is still the CURRENT active token.
    // If a stale background request gets rejected (token mismatch), the
    // current fresh session must NOT be cleared.
    if (
      error.response?.status === 401 &&
      error.response?.data?.detail === 'session_replaced'
    ) {
      const currentToken = localStorage.getItem('authToken');
      const requestToken = (originalRequest.headers?.Authorization || '').replace('Bearer ', '');
      if (!currentToken || currentToken === requestToken) {
        // The currently active token was rejected — genuine session replacement
        triggerLogout();
      }
      // else: stale request was rejected — current session is fine, do nothing
      return Promise.reject(error);
    }

    // For any other 401, attempt silent refresh (unless this IS the refresh call)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== '/auth/refresh'
    ) {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshSubscribers.push((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`,
          { refresh_token: refreshToken }
        );
        const { token } = response.data;
        localStorage.setItem('authToken', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        originalRequest.headers.Authorization = `Bearer ${token}`;
        onRefreshed(token);
        isRefreshing = false;
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        refreshSubscribers = [];
        const refreshStatus = refreshError.response?.status;
        if (refreshStatus === 401 || refreshStatus === 403) {
          // Only force logout if the token being refreshed is still the active one
          const currentToken = localStorage.getItem('authToken');
          const requestToken = (originalRequest.headers?.Authorization || '').replace('Bearer ', '');
          if (!currentToken || currentToken === requestToken) {
            triggerLogout();
          }
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);
// ─────────────────────────────────────────────────────────────────────────────

export default api;
